import {useState, type FormEvent} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Tag} from '@/components/ui/tag'
import {HelpIcon} from '@/components/help-icon'
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {ConfirmDialog} from '@/components/confirm-dialog'
import {
  ConsentForm,
  emptyConsent,
  validateConsent,
  type ConsentErrors,
  type ConsentValues,
} from '@/components/billing/consent-fields'
import {translateApiError} from '@/lib/errors'
import {formatDateOnly} from '@/lib/risk'
import {tokenizeWithWidget, WompiWidgetError, type WompiConfig} from '@/lib/wompi-widget'
import type {BillingPaymentSource, PaymentSourcePayload} from '@/hooks/inci/cartera/use-billing'

/** El caller es dueño del PUT/DELETE y los expone como promesas para mostrar éxito y error reales.
 * `card` es el panel del partner; `inline`, una línea dentro de la tarjeta del plan del tenant. */
export function AutoDebitoSection({
  source,
  wompi,
  defaultEmail,
  canEdit,
  putPending,
  deletePending,
  variant = 'card',
  nextChargeAt,
  onSave,
  onRemove,
}: {
  source: BillingPaymentSource | null
  wompi: WompiConfig
  defaultEmail: string
  canEdit: boolean
  putPending: boolean
  deletePending: boolean
  variant?: 'card' | 'inline'
  nextChargeAt?: string | null
  onSave: (payload: PaymentSourcePayload) => Promise<unknown>
  onRemove: () => Promise<unknown>
}) {
  const {t} = useTranslation()
  const [formOpen, setFormOpen] = useState(false)
  const [confirmRemove, setConfirmRemove] = useState(false)
  const [form, setForm] = useState<ConsentValues>({...emptyConsent, email: defaultEmail})
  const [fieldErrors, setFieldErrors] = useState<ConsentErrors>({})
  const [error, setError] = useState<string | null>(null)
  const [phase, setPhase] = useState<'idle' | 'wompi' | 'saving' | 'saved'>('idle')

  function openForm() {
    setForm({...emptyConsent, email: defaultEmail})
    setFieldErrors({})
    setError(null)
    setPhase('idle')
    setFormOpen(true)
  }

  function blurField(field: keyof ConsentValues) {
    setFieldErrors((fe) => ({...fe, [field]: validateConsent(form)[field]}))
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    const errors = validateConsent(form)
    setFieldErrors(errors)
    if (Object.values(errors).some(Boolean)) return
    // El modal de Radix atrapa el foco: se cierra antes de abrir Wompi o su iframe no recibe teclado.
    setFormOpen(false)
    setError(null)
    setPhase('wompi')
    try {
      const tokenized = await tokenizeWithWidget(wompi)
      if (tokenized === null) {
        setPhase('idle')
        return
      }
      setPhase('saving')
      await onSave({
        tipo: tokenized.tipo,
        token: tokenized.token,
        acceptance_token: tokenized.acceptanceToken,
        accept_personal_auth: tokenized.acceptPersonalAuth,
        customer_email: form.email,
        brand: tokenized.brand,
        last4: tokenized.last4,
        exp_month: tokenized.expMonth,
        exp_year: tokenized.expYear,
      })
      setPhase('saved')
    } catch (err) {
      setError(err instanceof WompiWidgetError ? t(err.message) : translateApiError(err))
      setPhase('idle')
    }
  }

  async function handleRemove() {
    setError(null)
    try {
      await onRemove()
      setConfirmRemove(false)
    } catch (err) {
      setConfirmRemove(false)
      setError(translateApiError(err))
    }
  }

  const gatewayReady = !!wompi.public_key
  const busy = phase === 'wompi' || phase === 'saving' || putPending

  function activeLabel(): string {
    if (!source) return ''
    if (source.tipo === 'nequi') {
      return source.last4
        ? t('planFacturacion.autoDebito.activeNequi', {last4: source.last4})
        : t('planFacturacion.autoDebito.activeNequiPlain')
    }
    // Ni el widget ni `POST /payment_sources` garantizan brand/last4 para tarjeta.
    if (!source.last4) return t('planFacturacion.autoDebito.activeCardPlain')
    const brand = source.brand ?? t('planFacturacion.autoDebito.activeCardPlain')
    const exp = source.exp_month && source.exp_year ? `${source.exp_month}/${source.exp_year}` : null
    return exp
      ? t('planFacturacion.autoDebito.activeCardExp', {brand, last4: source.last4, exp})
      : t('planFacturacion.autoDebito.activeCard', {brand, last4: source.last4})
  }

  const inline = variant === 'inline'
  const nextChargeNote =
    inline && nextChargeAt
      ? t('planFacturacion.autoDebito.nextChargeOn', {date: formatDateOnly(nextChargeAt)})
      : t('planFacturacion.autoDebito.nextChargeNote')

  return (
    <div className={inline ? 'space-y-2' : 'surface space-y-3 p-5'} data-testid="auto-debito-section">
      {!inline && (
        <div className="flex items-center gap-1.5">
          <h2 className="font-heading text-base text-foreground">{t('planFacturacion.autoDebito.title')}</h2>
          <HelpIcon label={t('planFacturacion.autoDebito.tipLabel')} testId="auto-debito-tip">
            {t('planFacturacion.autoDebito.body')}
          </HelpIcon>
        </div>
      )}

      {source ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2" data-testid="auto-debito-active">
          {/* Identidad del instrumento, no estado (U2(a)). */}
          <Tag tone="neutral">{activeLabel()}</Tag>
          <span className="text-xs text-muted-foreground">{nextChargeNote}</span>
          {canEdit && (
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="sm"
                data-testid="auto-debito-replace"
                disabled={!gatewayReady || busy}
                onClick={openForm}>
                {t('planFacturacion.autoDebito.replace')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                data-testid="auto-debito-remove"
                disabled={deletePending}
                onClick={() => setConfirmRemove(true)}>
                {t('planFacturacion.autoDebito.remove')}
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-3" data-testid="auto-debito-empty">
          <span className="text-sm text-muted-foreground">
            {t(inline ? 'planFacturacion.autoDebito.sinRenovacion' : 'planFacturacion.autoDebito.sinMetodo')}
          </span>
          {canEdit &&
            (gatewayReady ? (
              <Button
                variant={inline ? 'outline' : 'default'}
                size={inline ? 'sm' : 'default'}
                data-testid="auto-debito-add"
                disabled={busy}
                onClick={openForm}>
                {t('planFacturacion.autoDebito.add')}
              </Button>
            ) : (
              <span className="text-sm text-muted-foreground" data-testid="auto-debito-gateway-unconfigured">
                {t('planFacturacion.autoDebito.gatewayUnconfigured')}
              </span>
            ))}
        </div>
      )}

      {busy && (
        <p className="text-sm text-muted-foreground" data-testid="auto-debito-status">
          {phase === 'wompi' ? t('planFacturacion.autoDebito.enWompi') : t('planFacturacion.autoDebito.guardando')}
        </p>
      )}
      {phase === 'saved' && !busy && (
        <p className="text-sm text-primary" data-testid="auto-debito-saved">
          {t('planFacturacion.autoDebito.guardado')}
        </p>
      )}
      {error && (
        <p className="text-sm text-destructive" data-testid="auto-debito-error">
          {error}
        </p>
      )}

      <Dialog open={formOpen} onOpenChange={(open) => !busy && setFormOpen(open)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('planFacturacion.autoDebito.form.title')}</DialogTitle>
            <DialogDescription>{t('planFacturacion.autoDebito.form.subtitle')}</DialogDescription>
          </DialogHeader>
          <ConsentForm
            prefix="auto-debito"
            values={form}
            errors={fieldErrors}
            onChange={(patch) => {
              setForm((f) => ({...f, ...patch}))
              setFieldErrors((fe) => ({...fe, ...Object.fromEntries(Object.keys(patch).map((k) => [k, undefined]))}))
            }}
            onBlurField={blurField}
            onSubmit={handleSubmit}>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setFormOpen(false)} disabled={busy}>
                {t('planFacturacion.autoDebito.form.cancel')}
              </Button>
              <Button type="submit" data-testid="auto-debito-form-submit" disabled={busy}>
                {t('planFacturacion.autoDebito.form.submit')}
              </Button>
            </div>
          </ConsentForm>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmRemove}
        onOpenChange={setConfirmRemove}
        title={t('planFacturacion.autoDebito.removeConfirmTitle')}
        description={t('planFacturacion.autoDebito.removeConfirmBody')}
        confirmLabel={t('planFacturacion.autoDebito.removeConfirmCta')}
        cancelLabel={t('planFacturacion.autoDebito.form.cancel')}
        pending={deletePending}
        onConfirm={handleRemove}
      />
    </div>
  )
}
